/**
 * Unified API Service Layer
 * Provides clean interface for Django and Spring API calls
 */

import { djangoClient, springClient } from "./axiosClient";

/**
 * Generic request handler
 * @param {Object} client - Axios client instance (Django or Spring)
 * @param {String} method - HTTP method (GET, POST, PUT, DELETE, PATCH)
 * @param {String} url - API endpoint URL (without base URL)
 * @param {Object} data - Request payload (for POST, PUT, PATCH)
 * @param {Object} config - Additional axios config
 * @param {Boolean} isProtected - Whether the endpoint requires authentication
 * @returns {Promise} Response data
 */
const makeRequest = async (
  client,
  method,
  url,
  data = null,
  config = {},
  isProtected = true
) => {
  // Check for token if endpoint is protected
  if (isProtected && !localStorage.getItem("token")) {
    throw new Error("Authentication required. Please login.");
  }

  try {
    const requestConfig = {
      method,
      url,
      ...config,
      // Pass isProtected flag to interceptor
      _isProtected: isProtected,
    };

    // Add data for mutation methods
    if (data && ["POST", "PUT", "PATCH"].includes(method.toUpperCase())) {
      requestConfig.data = data;
    }
    requestConfig.params = { is_desktop: true, ...(config?.params || {}) };

    const response = await client.request(requestConfig);
    return response.data;
  } catch (error) {
    // Error is already handled by interceptor
    throw error;
  }
};

// ============================================================================
// DJANGO API SERVICE
// ============================================================================

export const djangoAPI = {
  /**
   * GET request to Django backend
   * @param {String} url - API endpoint
   * @param {Object} config - Additional config
   * @param {Boolean} isProtected - Requires authentication
   */
  get: (url, config = {}, isProtected = true) =>
    makeRequest(djangoClient, "GET", url, null, config, isProtected),

  /**
   * POST request to Django backend
   * @param {String} url - API endpoint
   * @param {Object} data - Request payload
   * @param {Object} config - Additional config
   * @param {Boolean} isProtected - Requires authentication
   */
  post: (url, data, config = {}, isProtected = true) =>
    makeRequest(djangoClient, "POST", url, data, config, isProtected),

  /**
   * PUT request to Django backend
   * @param {String} url - API endpoint
   * @param {Object} data - Request payload
   * @param {Object} config - Additional config
   * @param {Boolean} isProtected - Requires authentication
   */
  put: (url, data, config = {}, isProtected = true) =>
    makeRequest(djangoClient, "PUT", url, data, config, isProtected),

  /**
   * PATCH request to Django backend
   * @param {String} url - API endpoint
   * @param {Object} data - Request payload
   * @param {Object} config - Additional config
   * @param {Boolean} isProtected - Requires authentication
   */
  patch: (url, data, config = {}, isProtected = true) =>
    makeRequest(djangoClient, "PATCH", url, data, config, isProtected),

  /**
   * DELETE request to Django backend
   * @param {String} url - API endpoint
   * @param {Object} config - Additional config
   * @param {Boolean} isProtected - Requires authentication
   */
  delete: (url, config = {}, isProtected = true) =>
    makeRequest(djangoClient, "DELETE", url, null, config, isProtected),
};

// ============================================================================
// SPRING API SERVICE
// ============================================================================

export const springAPI = {
  /**
   * GET request to Spring backend
   * @param {String} url - API endpoint
   * @param {Object} config - Additional config
   * @param {Boolean} isProtected - Requires authentication
   */
  get: (url, config = {}, isProtected = true) =>
    makeRequest(springClient, "GET", url, null, config, isProtected),

  /**
   * POST request to Spring backend
   * @param {String} url - API endpoint
   * @param {Object} data - Request payload
   * @param {Object} config - Additional config
   * @param {Boolean} isProtected - Requires authentication
   */
  post: (url, data, config = {}, isProtected = true) =>
    makeRequest(springClient, "POST", url, data, config, isProtected),

  /**
   * PUT request to Spring backend
   * @param {String} url - API endpoint
   * @param {Object} data - Request payload
   * @param {Object} config - Additional config
   * @param {Boolean} isProtected - Requires authentication
   */
  put: (url, data, config = {}, isProtected = true) =>
    makeRequest(springClient, "PUT", url, data, config, isProtected),

  /**
   * PATCH request to Spring backend
   * @param {String} url - API endpoint
   * @param {Object} data - Request payload
   * @param {Object} config - Additional config
   * @param {Boolean} isProtected - Requires authentication
   */
  patch: (url, data, config = {}, isProtected = true) =>
    makeRequest(springClient, "PATCH", url, data, config, isProtected),

  /**
   * DELETE request to Spring backend
   * @param {String} url - API endpoint
   * @param {Object} config - Additional config
   * @param {Boolean} isProtected - Requires authentication
   */
  delete: (url, config = {}, isProtected = true) =>
    makeRequest(springClient, "DELETE", url, null, config, isProtected),
};

// ============================================================================
// UNIFIED API (Default to Spring for backward compatibility)
// ============================================================================

export const api = springAPI;

// Export default
export default {
  django: djangoAPI,
  spring: springAPI,
  api: springAPI, // default
};
